import { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useObservable } from '@ngneat/react-rxjs'
import Constants from '../../constants'
import { Mail as MailIcon } from '@mui/icons-material'
import dayjs from '../../utils/dayjs.utils'
import { sessionQuery, sessionService } from '../../store/session'
import { organizationsService } from '../../store/organizations'
import { cartsService } from '../../store/carts'

import { materialsService } from '../../store/materials'
import { catalogsService } from '../../store/catalogs'
import { ordersService } from '../../store/orders'
import useModal from '../../hooks/useModal.hooks'
import useSnackbar from '../../hooks/useSnackbar.hooks'
import { Mode } from '../../models/commons.models'
import { FilterItem, Filter, ItemType, Actions } from '../../models/props.models'
import {
  OrdersPagination,
  Order,
  OrderStatus,
  convertOrder2Imperials,
  convertOrder2Meters,
  Product,
} from '../../models/orders.models'
import { UserType } from '../../models/users.models'
import OrderList, { OrderListProps } from '../order/List.order'
import OrderModalAdmin from '../order/ModalAdmin.order'
import OrderModalDetails from '../order/ModalDetails.order'
import OrderModalDeny from '../order/ModalDeny.order'
import OrderModalComment from '../order/ModalComment.order'
import OrderModalFinish from '../order/ModalFinish.order'
import OrderModalClientAccept from '../order/ModalClientAccept.order'
import OrderModalFixPrices from '../order/ModalFixPrices.order'
import ModalFormChat from '../chat/ModalForm.chat'
import LoaderOverlay from '../layout/LoaderOverlay.layout'
import { ListRef } from '../common/List.common'
import { TermsOfSale } from '../../models/materials.models'
import Modal from '../layout/Modal.layout'
import { MessageFrom } from '../../models/chats.models'
import { Box } from '@mui/material'
import { getPlatform } from '../../models/platforms.models'
import DisplayModeButton from '../common/button/DisplayMode.button'
import OrderBuyerList from '../order/ListBuyer.order'

interface PageOrderListProps
  extends Omit<
    OrderListProps,
    | 'filter'
    | 'getValues'
    | 'onValueClick'
    | 'valueActions'
    | 'isValueSelected'
    | 'onValueSelect'
    | 'onFilterChange'
    | 'isCatalogPage'
  > {
  catalog?: string
  seller?: string
  clientOrganization?: string
  isCatalogOwner?: boolean
  useImperials: boolean,
  hideDisplayMode?: boolean,
}

type DisplayMode = 'order' | 'buyer' | 'material'

const PageOrderList: React.FC<PageOrderListProps> = (props) => {
  const {
    seller,
    clientOrganization,
    useImperials,
    isCatalogOwner,
    isClient,
    catalog,
    hideDisplayMode,
    ...listProps
  } = props

  const listRef = useRef<ListRef | null>(null)
  const show = useSnackbar()
  const { t } = useTranslation()
  const [modal, setModal] = useModal<string>()
  const [loading, setLoading] = useState<boolean>(false)
  const [displayMode, setDisplayMode] = useState<DisplayMode>('order')
  const [openedOrder, setOpenedOrder] = useState<Order>()
  const [user] = useObservable(sessionQuery.user)
  const adminPlatform =
    Constants.mode === Mode.admin
      ? sessionService.getAdminPlatform()
      : undefined

  useEffect(() => {
    const getModal = async () => {
      if (modal) {
        setLoading(true)
        try {
          const order = await ordersService.getOrderById(
            modal
              .replace('chat.', '')
              .replace('admin.', '')
              .replace('deny.', '')
              .replace('clientPending.', '')
              .replace('clientAccept.', '')
              .replace('fixPrices.', '')
              .replace('finish.', '')
              .replace('sellerAccept.', '')
              .replace('cancel.', '')
              .replace('comment.', '')
              .replace('commentFromDetails.', '')
              .replace('deleteComment.', '')
          )

          setOpenedOrder(useImperials ? convertOrder2Imperials(order) : order)
        } catch (err: any) {
          show(err)
        }
        setLoading(false)
      } else {
        setOpenedOrder(undefined)
      }
    }
    getModal()
  }, [show, modal, useImperials])

  const parseFilter = useCallback(
    (filters: any) => {
      const catalogOwner = filters.catalogOwner
        ? filters.catalogOwner.value
        : undefined
      let periodStart: any = undefined
      let periodEnd: any = undefined
      let date: any = undefined
      if (filters.periodStart) {
        date = dayjs(filters.periodStart)
        if (date.isValid()) {
          periodStart = date.startOf('month').toDate()
          periodEnd = date.endOf('month').toDate()
        }
      }
      return {
        ...filters,
        periodStart,
        periodEnd,
        upToDate: !!filters.unReadMessage ? false : undefined,
        owned: Constants.mode !== Mode.admin,
        catalog: catalog
          ? catalog
          : filters.catalog
          ? filters.catalog?.value
          : undefined,
        catalogOwner:
          seller ?? (isCatalogOwner ? user?.organization._id : catalogOwner),
        clientOrganization:
          clientOrganization ?? filters.clientOrganization?.value,
        client: isClient && !clientOrganization ? user?._id : undefined,
      }
    },
    [seller, clientOrganization, catalog, user, isClient, isCatalogOwner],
  )

  const getOrders = useCallback(
    async (filters: any): Promise<OrdersPagination> => {
      return await ordersService.getOrders(parseFilter(filters))
    },
    [parseFilter],
  )

  const getExport = useCallback(
    async (filters: any) => {
      setLoading(true)

      try {
        await ordersService.getExport(parseFilter(filters))
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    },
    [show, parseFilter],
  )
  const onDownload = useCallback(
    async (order: Order, type: 'order' | 'transfer' = 'order') => {
      setLoading(true)
      try {
        if (type === 'transfer') {
          await ordersService.getTransferDocument(order)
        } else {
          await ordersService.getOrderPdf(order)
        }
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    },
    [show],
  )
  const onUpdate = useCallback(
    async (order: Order, update: { status: OrderStatus }) => {
      setLoading(true)
      try {
        await ordersService.updateOrder(order, update)
        if (update.status === OrderStatus.clientPending) {
          show(t('orders:actions.clientPending.success'), 'success')
        } else if (update.status === OrderStatus.accepted) {
          show(t('orders:actions.accept.success'), 'success')
        } else if (update.status === OrderStatus.canceled) {
          show(t('orders:actions.cancel.success'), 'success')
        }
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    },
    [show, t],
  )
  const addToCart = useCallback(
    async (order: Order) => {
      setLoading(true)
      try {
        for (let i = 0; i < order.products.length; i++) {
          try {
            //check if public
            await materialsService.getMaterial(
              order.products[i].material._id,
              true,
            )
            cartsService.addToCart(
              order.products[i].material._id,
              order.products[i].quantity,
            )
          } catch (err) {}
        }
        show(t('orders:actions.addToCart.success'), 'success')
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    },
    [show, t],
  )

  const filter = useMemo<Filter>(() => {
    const lg = Constants.mode === Mode.admin || isClient ? 4 : 2

    return {
      items: [
        {
          type: ItemType.search,
          key: 'search',
          grid: { lg, sm: 6, xs: 12 },
          props: {
            placeholder: t('orders:components.list.searchFilter'),
          },
        },
        ...((Constants.mode === Mode.admin && !catalog && !seller
          ? [
              {
                type: ItemType.asyncSelect,
                key: 'catalogOwner',
                grid: { lg, sm: 6, xs: 12 },
                props: {
                  placeholder: t('orders:components.list.catalogOwnerFilter'),
                  minimumSearchLength: 3,
                  getOptions: (search?: string) =>
                    organizationsService.getOrganizationOptions(search),
                },
              },
            ]
          : []) as FilterItem[]),
        ...((!clientOrganization && !isClient
          ? [
              {
                type: ItemType.asyncSelect,
                key: 'clientOrganization',
                grid: { lg, sm: 6, xs: 12 },
                props: {
                  placeholder: t(
                    'orders:components.list.clientOrganizationFilter',
                  ),
                  minimumSearchLength: 3,
                  getOptions: (search?: string) =>
                    organizationsService.getOrganizationOptions(search),
                },
              },
            ]
          : []) as FilterItem[]),
        ...(((Constants.mode === Mode.admin || isCatalogOwner) && !catalog
          ? [
              {
                type: ItemType.asyncSelect,
                key: 'catalog',
                grid: { lg, sm: 6, xs: 12 },
                props: {
                  placeholder: t('orders:components.list.catalog'),
                  minimumSearchLength: 3,
                  getOptions: (search?: string) =>
                    catalogsService.getCatalogOptions({ search }),
                },
              },
            ]
          : []) as FilterItem[]),
        {
          type: ItemType.date,
          key: 'periodStart',
          grid: { md: 2, xs: 6 },
          props: {
            format: 'MM/YYYY',
            blurredFormat: true,
          },
        },
        ...((Constants.mode !== Mode.app
          ? [
              {
                type: ItemType.checkbox,
                key: 'unReadMessage',
                grid: { md: 2, xs: 6 },
                props: {
                  label: t('orders:components.list.unreadFilter'),
                },
              },
            ]
          : []) as FilterItem[]),
        ...((!isClient && displayMode === 'order'
          ? [
              {
                type: ItemType.button,
                key: 'getExport',
                grid: { md: 2, sm: 6, xs: 12 },
                props: {
                  fullWidth: true,
                  children: t('orders:components.list.getExport'),
                  onClick: async () => getExport(listRef.current?.filter ?? {}),
                },
              },
            ]
          : []) as FilterItem[]),
      ],
    }
  }, [
    t,
    catalog,
    isClient,
    getExport,
    isCatalogOwner,
    seller,
    clientOrganization,
    displayMode
  ])

  const checkAction = useCallback(
    (type: 'seller' | 'client', order: Order) => {
      const isAdmin =
        user?.type === UserType.admin ||
        (user?.type === UserType.platformAdmin &&
          user?.platformOwned === order.catalog.organization.platform)
      if (type === 'seller') {
        return (
          isAdmin || user?.organization._id === order.catalog.organization._id
        )
      }
      return user?._id === order.client._id || isAdmin
    },
    [user],
  )

  const orderActions = useCallback(
    (order: Order): Actions | undefined => {
      const orderPlatform = getPlatform(order.catalog.organization.platform)

      const actions = [
        ...(Constants.mode === Mode.admin &&
        order.status !== OrderStatus.canceled &&
        order.status !== OrderStatus.denied &&
        order.status !== OrderStatus.finished
          ? [
              {
                label: t('orders:actions.update.label'),
                onClick: () => {
                  setModal(`admin.${order._id}`)
                },
              },
            ]
          : []),
        ...(Constants.mode === Mode.app
          ? []
          : [
              {
                label: t('chats:actions.goTo'),
                onClick: () => {
                  setModal(`chat.${order._id}`)
                },
              },
            ]),
        ...(isClient
          ? [
              {
                label: t('orders:actions.addToCart.label'),
                onClick: () => {
                  addToCart(order)
                },
              },
            ]
          : []),
        ...(order.status !== OrderStatus.denied &&
        order.status !== OrderStatus.canceled &&
        order.status !== OrderStatus.finished
          ? [
              {
                label: t('orders:actions.downloadPdf.label'),
                onClick: () => {
                  onDownload(order)
                },
              },
            ]
          : []),
        ...((order.status === OrderStatus.accepted ||
          order.status === OrderStatus.finished) &&
        orderPlatform?.hasTransferDocument &&
        checkAction('seller', order)
          ? [
              {
                label: t('orders:actions.downloadTransferDocument.label'),
                onClick: async () => {
                  onDownload(order, 'transfer')
                },
              },
            ]
          : []),
        ...(order.status === OrderStatus.accepted &&
        checkAction('seller', order)
          ? [
              {
                label: t('orders:actions.finish.label'),
                onClick: async () => {
                  setModal(`finish.${order._id}`)
                },
              },
            ]
          : []),
        ...(order.status === OrderStatus.pricePending &&
        checkAction('seller', order)
          ? [
              {
                label: t('orders:actions.fixPrices.label'),
                onClick: () => {
                  setModal(`fixPrices.${order._id}`)
                },
              },
            ]
          : []),
        ...(((order.status === OrderStatus.pending && !order.priceModified) ||
          order.status === OrderStatus.clientAccepted) &&
        checkAction('seller', order)
          ? [
              {
                label: t('orders:actions.accept.label'),
                onClick: () => {
                  setModal(`sellerAccept.${order._id}`)
                },
              },
            ]
          : []),
        ...(order.status === OrderStatus.pending &&
        order.priceModified &&
        checkAction('seller', order)
          ? [
              {
                label: t('orders:actions.clientPending.label'),
                onClick: async () => {
                  setModal(`clientPending.${order._id}`)
                },
              },
            ]
          : []),
        ...((order.status === OrderStatus.clientAccepted ||
          order.status === OrderStatus.pending ||
          order.status === OrderStatus.pricePending ||
          order.status === OrderStatus.clientPending ||
          order.status === OrderStatus.accepted) &&
        checkAction('seller', order)
          ? [
              {
                label: t('orders:actions.deny.label'),
                onClick: async () => {
                  setModal(`deny.${order._id}`)
                },
              },
            ]
          : []),
        ...(order.status === OrderStatus.clientPending &&
        checkAction('client', order)
          ? [
              {
                label: t('orders:actions.clientAccept.label'),
                onClick: async () => {
                  setModal(`clientAccept.${order._id}`)
                },
              },
            ]
          : []),
        ...((order.status === OrderStatus.clientPending ||
          order.status === OrderStatus.pending ||
          order.status === OrderStatus.pricePending) &&
        checkAction('client', order)
          ? [
              {
                label: t('orders:actions.cancel.label'),
                onClick: () => {
                  setModal(`cancel.${order._id}`)
                },
              },
            ]
          : []),
      ]

      return actions.length > 0 ? { items: actions } : undefined
    },
    [t, onDownload, checkAction, setModal, addToCart, isClient],
  )

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {loading && <LoaderOverlay />}
      {!hideDisplayMode && (
        <Box sx={{ padding: 2 }}>
          <DisplayModeButton
            selected={displayMode === 'buyer'}
            onClick={() => setDisplayMode('buyer')}>
            {t('orders:components.displayMode.buyer')}
          </DisplayModeButton>

          <DisplayModeButton
            selected={displayMode === 'order'}
            onClick={() => setDisplayMode('order')}>
            {t('orders:components.displayMode.order')}
          </DisplayModeButton>
        </Box>
      )}
      {displayMode === 'buyer' && (
        <OrderBuyerList
          getValues={getOrders}
          isClient={isClient}
          adminPlatform={adminPlatform}
          isCatalogPage={!!catalog}
          valueActions={orderActions}
          ref={listRef}
          filter={filter}
          disabledPagination={Constants.mode !== Mode.admin}
          onValueClick={(order: Order) => setModal(order._id)}
          onCommentClick={(order) => setModal(`comment.${order._id}`)}
        />
      )}

      {displayMode === 'order' && (
        <OrderList
          {...listProps}
          valueIcon={
            Constants.mode !== Mode.app
              ? (order?: Order) => {
                  if (
                    ((isClient || Constants.mode === Mode.admin) &&
                      order?.clientChatUpToDate === false) ||
                    ((!isClient || Constants.mode === Mode.admin) &&
                      order?.sellerChatUpToDate === false)
                  ) {
                    return (
                      <MailIcon color="primary" onClick={() => setModal(`chat.${order?._id}`)} />
                    )
                  }
                  return undefined
                }
              : undefined
          }
          adminPlatform={adminPlatform}
          disableTableScroll={!!catalog}
          isCatalogPage={!!catalog}
          disabledPagination={Constants.mode !== Mode.admin}
          ref={listRef}
          isClient={isClient}
          isAdmin={Constants.mode === Mode.admin}
          isSeller={!isClient && Constants.mode !== Mode.admin}
          getValues={getOrders}
          onValueClick={(order: Order) => setModal(order._id)}
          filter={filter}
          valueActions={orderActions}
          onAction={(action) => setModal(action)}
        />
      )}
      {modal &&
        openedOrder &&
        !modal.includes('chat') &&
        !modal.includes('deny') &&
        !modal.includes('clientPending') &&
        !modal.includes('clientAccept') &&
        !modal.includes('fixPrices') &&
        !modal.includes('sellerAccept') &&
        !modal.includes('finish') &&
        !modal.includes('admin') &&
        !modal.includes('cancel') &&
        !modal.includes('comment') &&
        !modal.includes('deleteComment') && (
          <OrderModalDetails
            adminPlatform={adminPlatform}
            order={openedOrder}
            isClient={isClient}
            onClose={() => setModal('')}
            onEditComment={() => setModal(`commentFromDetails.${openedOrder._id}`)}
          />
        )}
      {modal && openedOrder && modal.includes('sellerAccept') && (
        <Modal
          onClose={() => setModal('')}
          onConfirm={async () => {
            await onUpdate(openedOrder, { status: OrderStatus.accepted })
            listRef?.current?.updateValues()
          }}
          title={t('orders:actions.accept.label')}
        />
      )}
      {modal && openedOrder && modal.includes('cancel') && (
        <Modal
          onClose={() => setModal('')}
          onConfirm={async () => {
            await onUpdate(openedOrder, { status: OrderStatus.canceled })
            listRef?.current?.updateValues()
          }}
          title={t('orders:actions.cancel.label')}
        />
      )}
      {modal && openedOrder && modal.includes('clientPending') && (
        <Modal
          onClose={() => setModal('')}
          onConfirm={async () => {
            await onUpdate(openedOrder, { status: OrderStatus.clientPending })
            listRef?.current?.updateValues()
          }}
          title={t('orders:actions.clientPending.label')}
        />
      )}
      {modal && openedOrder && modal.includes('deny') && (
        <OrderModalDeny
          onSubmit={async (data: any) => {
            await ordersService.updateOrder(openedOrder, {
              status: OrderStatus.denied,
              deniedFor: data.deniedFor,
            })
          }}
          onSuccess={async () => {
            show(t('orders:actions.deny.success'), 'success')
            listRef?.current?.updateValues()
          }}
          onClose={() => setModal('')}
        />
      )}
      {modal && openedOrder && modal.includes('finish') && (
        <OrderModalFinish
          order={openedOrder}
          useImperials={useImperials}
          onSubmit={async (data: any) => {
            let orderData = {
              status: OrderStatus.finished,
              removalOrder: data.removalOrder,
              finishedComment: data.finishedComment,
              discount: data.discount,
              products: data.products,
            }
            orderData = useImperials ? convertOrder2Meters(orderData as Order) : orderData

            await ordersService.updateOrder(openedOrder, {
              ...orderData,
              products: orderData.products.map((product: Product) => {
                return {
                  material: product.material._id,
                  termsOfSale: product.termsOfSale,
                  price: product.termsOfSale === TermsOfSale.sale ? product.price : undefined,
                  discount: product.termsOfSale === TermsOfSale.sale ? product.discount : undefined,
                  quantity: product.quantity,
                }
              }),
            })
          }}
          onSuccess={async () => {
            show(t('orders:actions.finish.success'), 'success')
            listRef?.current?.updateValues()
          }}
          onClose={() => setModal('')}
        />
      )}
      {modal && openedOrder && modal.includes('clientAccept') && (
        <OrderModalClientAccept
          order={openedOrder}
          onSubmit={async (data: any) => {
            let orderData = {
              status: OrderStatus.clientAccepted,
              products: data.products,
            }
            orderData = useImperials ? convertOrder2Meters(orderData as Order) : orderData
            await ordersService.updateOrder(openedOrder, {
              ...orderData,
              products: orderData.products.map((product: Product) => {
                return {
                  material: product.material._id,
                  quantity: product.quantity,
                }
              }),
            })
          }}
          onSuccess={async () => {
            show(t('orders:actions.clientAccept.success'), 'success')
            listRef?.current?.updateValues()
          }}
          onClose={() => setModal('')}
        />
      )}
      {modal && openedOrder && modal.includes('fixPrices') && (
        <OrderModalFixPrices
          order={openedOrder}
          onSuccess={() => {
            show(t('orders:actions.fixPrices.success'), 'success')
            listRef?.current?.updateValues()
          }}
          onClose={() => setModal('')}
          useImperials={useImperials}
        />
      )}
      {modal && openedOrder && modal.includes('chat') && (
        <ModalFormChat
          order={openedOrder}
          updateList={() => listRef?.current?.updateValues()}
          from={
            Constants.mode === Mode.admin
              ? 'admin'
              : isClient
                ? MessageFrom.client
                : MessageFrom.seller
          }
          onClose={() => setModal('')}
        />
      )}
      {modal &&
        openedOrder &&
        (modal.includes('comment.') || modal.includes('commentFromDetails.')) && (
          <OrderModalComment
            comment={openedOrder.comment}
            onSubmit={async (data: any) =>
              await ordersService.updateOrder(openedOrder, {
                comment: data.comment,
              })
            }
            actions={
              openedOrder.comment && openedOrder.comment?.length > 0
                ? [
                    {
                      onClick: () => setModal(`deleteComment.${openedOrder._id}`),
                      color: 'error',
                      label: t('global:actions.delete'),
                    },
                  ]
                : []
            }
            onSuccess={async () => {
              show(t('orders:actions.comment.success'), 'success')
              listRef?.current?.updateValues()
            }}
            onClose={() => setModal(modal.includes(`commentFromDetails.`) ? openedOrder._id : '')}
          />
        )}
      {modal && openedOrder && modal.includes('deleteComment') && (
        <Modal
          title={t('orders:components.modalCommentDelete.title')}
          onConfirm={async () => {
            await ordersService.updateOrder(openedOrder, {
              comment: '',
            })
          }}
          onSuccess={async () => {
            show(t('orders:actions.comment.deleteSuccess'), 'success')
            listRef?.current?.updateValues()
          }}
          onClose={() => setModal('')}
        />
      )}

      {modal && openedOrder && modal.includes('admin') && (
        <OrderModalAdmin
          order={openedOrder}
          useImperials={useImperials}
          onSubmit={async (data: any) => {
            let orderData = {
              discount: data.discount,
              products: data.products,
            }
            orderData = useImperials ? convertOrder2Meters(orderData as Order) : orderData

            await ordersService.updateOrder(openedOrder, {
              ...orderData,
              products: orderData.products.map((product: Product) => {
                return {
                  material: product.material._id,
                  termsOfSale: product.termsOfSale,
                  price: product.termsOfSale === TermsOfSale.sale ? product.price : undefined,
                  discount: product.termsOfSale === TermsOfSale.sale ? product.discount : undefined,
                  quantity: product.quantity,
                }
              }),
            })
          }}
          onSuccess={async () => {
            show(t('orders:actions.finish.success'), 'success')
            listRef?.current?.updateValues()
          }}
          onClose={() => setModal('')}
        />
      )}
    </Box>
  )
}
export default PageOrderList
